import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { validateEmail } from "@/lib/emailValidation";
import { Mail, AlertCircle } from "lucide-react";

interface ChangeStudentEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  studentName?: string | null;
  currentEmail?: string | null;
  onSuccess?: (newEmail: string) => void;
}

export function ChangeStudentEmailDialog({
  open, onOpenChange, userId, studentName, currentEmail, onSuccess,
}: ChangeStudentEmailDialogProps) {
  const { toast } = useToast();
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setNewEmail(""); setConfirmEmail(""); setError(null); setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    setError(null);
    if (!userId) { setError("Missing user reference"); return; }
    const a = newEmail.trim().toLowerCase();
    const b = confirmEmail.trim().toLowerCase();
    if (a !== b) { setError("The two email addresses do not match"); return; }
    const v = validateEmail(a);
    if (!v.valid) { setError(v.error || "Invalid email"); return; }
    if (currentEmail && a === currentEmail.trim().toLowerCase()) {
      setError("The new email is the same as the current one"); return;
    }

    setSubmitting(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/admin-change-user-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_id: userId, new_email: a }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to update email");

      toast({
        title: "Sign-in email updated",
        description: `${studentName ?? "The student"} can now log in with ${a}.`,
      });
      onSuccess?.(a);
      onOpenChange(false);
    } catch (e: any) {
      setError(e.message || "Failed to update email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Change Sign-in Email
          </DialogTitle>
          <DialogDescription>
            Update the login email for <strong>{studentName ?? "this student"}</strong>. They will use the new
            address to sign in immediately — no verification step required.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Current email</Label>
            <Input value={currentEmail ?? "—"} readOnly className="bg-muted font-mono text-sm" />
          </div>

          <div className="space-y-1">
            <Label htmlFor="new-email">New email</Label>
            <Input
              id="new-email" type="email" autoComplete="off"
              value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
              placeholder="student@example.com"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="confirm-email">Confirm new email</Label>
            <Input
              id="confirm-email" type="email" autoComplete="off"
              value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder="Re-type the new email"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !newEmail || !confirmEmail}>
            {submitting ? "Updating..." : "Update Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
